import React from 'react';
import { useTranslation } from 'react-i18next';
import PrimaryBtn from '../shared/Buttons/PrimaryButton';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Button, useMediaQuery } from '@mui/material';
import WarningButton from '../shared/Buttons/WarningButton';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { useNavigate } from 'react-router-dom';
import useConfirmDialog from '../../hooks/dialogs/useConfirmDialog';
import useFullScreenLoading from '../../hooks/useFullScreenLoading';
import { QuotationSummaryData } from '../../models/quotations/Quotations';
import useQuotations from '../../hooks/quotations/useQuotations';
import useHandleBulkProductsToCart from '../../hooks/cart/useHandleBulkProductsToCart';

const StyledBox = styled(Box)(({ theme }) => ({
    paddingBottom: '15px',
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    '@media (max-width: 650px)': {
        display: 'grid',
        width: '100%',
        gap: '8px',
        gridTemplateColumns: 'repeat(3, 1fr)',
    },
}));

const StyledDeleteButton = styled(Button)(({ theme }) => ({
    color: theme.palette.error[600],
    cursor: 'pointer',
    height: '40px',
    '@media (max-width: 450px)': { fontSize: '14px', padding: '6px 0' },
}));

export const QuotationDetailsActions: React.FC<{
    quote: QuotationSummaryData;
}> = (props) => {
    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS',
    });
    const { quote } = props;
    const navigate = useNavigate();
    const confirmDialog = useConfirmDialog();
    const fullScreenLoading = useFullScreenLoading();
    const quotations = useQuotations();
    const handleBulkProductsToCart = useHandleBulkProductsToCart();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleAddToCart = () => {
        handleBulkProductsToCart.handleAddToCart(
            quotations.convertProductsToCart(quote.items),
            'QUOTATION_DETAILS.PRODUCTS_ADDED_DIALOG',
            'quotations'
        );
    };

    const handleEditAction = () => {
        navigate('/quotation/edit/' + quote.id);
    };

    const copyQuotation = () => {
        fullScreenLoading.setFullScreenLoading(true);
        quotations
            .copyQuotation(quote.id)
            .then(() => {
                SnackbarUtils.success(t('MESSAGES.COPIED'));
                navigate('/quotations');
            })
            .catch(() => {
                SnackbarUtils.error(t('MESSAGES.COPIED_ERROR'));
            })
            .finally(() => fullScreenLoading.setFullScreenLoading(false));
    };

    const handleCopyAction = () => {
        confirmDialog.showDialog({
            title: t('QUOTATION_COPY_CONFIRM_DIALOG.TITLE'),
            content: t('QUOTATION_COPY_CONFIRM_DIALOG.CONTENT'),
            confirmText: t('QUOTATION_COPY_CONFIRM_DIALOG.CONFIRM'),
            cancelText: t('QUOTATION_COPY_CONFIRM_DIALOG.CANCEL'),
            onConfirm: () => copyQuotation(),
        });
    };

    const deleteQuotation = () => {
        quotations
            .deleteQuotation(quote.id)
            .then(() => {
                SnackbarUtils.success(t('MESSAGES.DELETED'));
                navigate('/quotations');
            })
            .catch(() => {
                SnackbarUtils.error(t('MESSAGES.DELETED_ERROR'));
            });
    };

    const handleClickDelete = () => {
        confirmDialog.showDialog({
            title: t('QUOTATION_DELETE_CONFIRM_DIALOG.TITLE'),
            content: t('QUOTATION_DELETE_CONFIRM_DIALOG.CONTENT'),
            confirmText: t('QUOTATION_DELETE_CONFIRM_DIALOG.CONFIRM'),
            cancelText: t('QUOTATION_DELETE_CONFIRM_DIALOG.CANCEL'),
            onConfirm: () => deleteQuotation(),
        });
    };

    const toPDF = () => {
        quotations
            .createPDFQuotation(quote.id, quote.use_logo ? 1 : 0)
            .then((resp) => {
                // let arrBuffer = base64ToArrayBuffer(resp);
                const url = window.URL.createObjectURL(
                    new Blob([resp], { type: 'application/pdf' })
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', quote.name + '-arajanlat');

                // Append to html link element page
                document.body.appendChild(link);

                // Start download
                link.click();

                // Clean up and remove the link
                link.parentNode.removeChild(link);
            })
            .catch(() => {});
    };

    const getProductNumber = () => {
        return quote.items.reduce(function (sum, quoteItem) {
            return sum + quoteItem.quantity;
        }, 0);
    };

    return (
        <StyledBox>
            <PrimaryBtn
                onClick={handleEditAction}
                sx={{ '@media (max-width: 400px)': { padding: '6px 0' } }}
            >
                {t('QUOTATION_DETAILS.ACTIONS.EDIT', {
                    number: quote.items.length,
                })}
            </PrimaryBtn>
            <PrimaryBtn
                onClick={handleCopyAction}
                sx={{ '@media (max-width: 400px)': { padding: '6px 0' } }}
            >
                {t('QUOTATION_DETAILS.ACTIONS.COPY')}
            </PrimaryBtn>
            <PrimaryBtn
                onClick={handleAddToCart}
                disabled={quote?.items?.length < 1}
                sx={{ '@media (max-width: 400px)': { padding: '6px 0' } }}
            >
                {t('QUOTATION_DETAILS.ACTIONS.ADD_TO_CART', {
                    number: quote.items.length,
                })}{' '}
                ({getProductNumber()})
            </PrimaryBtn>

            <WarningButton
                onClick={toPDF}
                disabled={quote?.items?.length < 1}
                sx={{ '@media (max-width: 400px)': { padding: '6px 0' } }}
            >
                {t('QUOTATION_DETAILS.ACTIONS.GENERATE_PDF')}
            </WarningButton>
            <StyledDeleteButton
                variant="outlined"
                color="error"
                onClick={handleClickDelete}
            >
                {isMobile
                    ? t('QUOTATION_DETAILS.ACTIONS.SHORT_DELETE')
                    : t('QUOTATION_DETAILS.ACTIONS.DELETE')}
            </StyledDeleteButton>
        </StyledBox>
    );
};
